import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormErrorMessage,
  Stack,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { Feature } from '@shinetools/pricing-plan-library';
import { Reorder } from 'framer-motion';

import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import { ERROR_MESSAGE_CLASSNAME } from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import { createEmptyTask } from 'features/Work/constants';
import { InvoiceFormSchema } from 'features/Work/form/invoiceForm.schema';
import { QuoteFormSchema } from 'features/Work/form/quoteForm.schema';
import { FullTasksFormSchema } from 'features/Work/form/tasksForm.schema';
import { CatalogItem } from 'features/Work/hooks/useCatalogItemsQuery';

import AddNewButton from './components/AddNewButton';
import CatalogItemsListModal from './components/CatalogItemsListModal';
import CreateCard from './components/CreateCard';
import ReorderingHeader from './components/ReorderingHeader';
import TaskForm from './components/TaskForm';
import locales from './locales';

const TasksContent = () => {
  const { isFeatureAvailable } = useCurrentPricingPlan();
  const {
    isOpen: isCatalogItemsModalOpen,
    onClose: onCloseCatalogItemsModal,
    onOpen: onOpenCatalogItemsModal,
  } = useDisclosure();

  const { control, formState, getValues, setValue } = useFormContext<
    InvoiceFormSchema | QuoteFormSchema
  >();

  const { fields: tasks } = useFieldArray({
    control,
    name: 'tasks',
  });

  const addTask = (values?: Partial<FullTasksFormSchema>) => {
    const newTask = { ...createEmptyTask(), ...values };
    const newTasks = [...getValues('tasks'), newTask];

    setValue('tasks', newTasks);
  };

  const deleteTask = (taskIndex: number) => {
    const newTasks = getValues('tasks');
    newTasks.splice(taskIndex, 1);

    setValue('tasks', newTasks);
  };

  const addTaskFromCatalogItem = (catalogItems: CatalogItem[]) => {
    const newTasks = catalogItems.map((catalogItem) => ({
      ...createEmptyTask(),
      invoiceCatalogItemId: catalogItem.invoiceCatalogItemId,
      subtitle: catalogItem.subtitle,
      title: catalogItem.title,
      type: catalogItem.type,
      unitPrice: catalogItem.unitPrice,
      vatPercentage: catalogItem.vatPercentage,
    }));

    setValue('tasks', [...getValues('tasks'), ...newTasks]);
  };

  const isItemsCatalogEnabled = isFeatureAvailable(
    Feature.InvoicingItemsCatalog,
  );

  const handleTaskOrderChange = (index: number, direction: 'up' | 'down') => {
    const newTasksOrder = getValues('tasks');
    const directionIndex = direction === 'up' ? -1 : 1;

    // @ts-expect-error newTasksOrder items accessed by index are possibly 'undefined'.
    [newTasksOrder[index], newTasksOrder[index + directionIndex]] = [
      newTasksOrder[index + directionIndex],
      newTasksOrder[index],
    ];
    setValue('tasks', newTasksOrder);
  };

  const tasksError = formState.errors?.tasks?.message;

  return (
    <>
      {tasks.length === 0 && isItemsCatalogEnabled ? (
        <Stack spacing="space-16">
          <FormControl isInvalid={!!tasksError}>
            {tasksError ? (
              <FormErrorMessage className={ERROR_MESSAGE_CLASSNAME}>
                {tasksError}
              </FormErrorMessage>
            ) : null}
          </FormControl>

          <VStack gap="space-10">
            <CreateCard
              description={locales.fromCatalog.description}
              icon="tag"
              onClick={onOpenCatalogItemsModal}
              title={locales.fromCatalog.title}
            />

            <CreateCard
              description={locales.oneOff.description}
              icon="add-file"
              onClick={() => addTask()}
              title={locales.oneOff.title}
            />
          </VStack>
        </Stack>
      ) : (
        <>
          <Reorder.Group onReorder={() => null} values={tasks}>
            <Stack paddingBottom="space-40" spacing="space-16">
              {tasks.map((task, index) => (
                <Reorder.Item
                  as="div"
                  dragListener={false}
                  key={task.invoiceTaskId}
                  value={task}
                >
                  <SunshineCard.Group>
                    {tasks.length > 1 ? (
                      <ReorderingHeader
                        handleTaskOrderChange={handleTaskOrderChange}
                        hasTagIcon={!!task.invoiceCatalogItemId}
                        index={index}
                        moveDownIsDisabled={index === tasks.length - 1}
                        moveUpIsDisabled={index === 0}
                      />
                    ) : null}

                    <TaskForm
                      allowDelete={isItemsCatalogEnabled || tasks.length !== 1}
                      index={index}
                      isFromCatalog={Boolean(task.invoiceCatalogItemId)}
                      onDelete={() => deleteTask(index)}
                    />
                  </SunshineCard.Group>
                </Reorder.Item>
              ))}
            </Stack>
          </Reorder.Group>

          <AddNewButton
            isItemsCatalogEnabled={isItemsCatalogEnabled}
            onAddFromCatalog={onOpenCatalogItemsModal}
            onCreateOneOff={addTask}
          />
        </>
      )}

      {isCatalogItemsModalOpen ? (
        <CatalogItemsListModal
          isOpen
          onClose={onCloseCatalogItemsModal}
          onSave={addTaskFromCatalogItem}
        />
      ) : null}
    </>
  );
};

export default TasksContent;
